import { bytesToBase64Url, base64UrlToBytes, toBufferSource } from './encoding.js'

/**
 * The short-lived protected delivery payload — PRD §10.2.1, §15.5.
 *
 * An authentication code exists at rest only as ciphertext under a key derived from the
 * deployment secret, and only until it is purged. Two Cloudflare services touch it: the API
 * seals and stores it (`auth_delivery_payloads`), and the mail worker unseals it at send time
 * to render the message — so the derivation and the stored format live here, in one place,
 * rather than in either service. A second implementation of the derivation would be a payload
 * the deliverer cannot read: the failure mode is silent (no mail), not loud.
 *
 * The derivation is HKDF-SHA-256 over the deployment secret with a versioned salt and the
 * profile as `info`; the stored form `v1.<iv>.<ciphertext>` is self-describing so a key or
 * profile rotation is detectable rather than mis-decrypted.
 */
const encoder = new TextEncoder()

export const PAYLOAD_ENCRYPTION_PROFILE = 'signbento:auth-delivery-payload:aes-256-gcm:v1'
export const PAYLOAD_CONTENT_PROFILE_VERSION = 1

const PAYLOAD_KEY_SALT = encoder.encode('signbento:auth-payload-key:v1')

/** Derives the payload key from the environment secret; the secret itself is never used raw. */
async function payloadKey(secret: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    toBufferSource(encoder.encode(secret)),
    'HKDF',
    false,
    ['deriveBits'],
  )
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: toBufferSource(PAYLOAD_KEY_SALT),
      info: toBufferSource(encoder.encode(PAYLOAD_ENCRYPTION_PROFILE)),
    },
    material,
    256,
  )
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

/** `v1.<iv>.<ciphertext>` in base64url — self-describing so a key rotation is detectable. */
export async function encryptDeliveryPayload(
  secret: string,
  plaintext: string,
  iv: Uint8Array,
): Promise<string> {
  const key = await payloadKey(secret)
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv) },
    key,
    toBufferSource(encoder.encode(plaintext)),
  )
  return `v1.${bytesToBase64Url(iv)}.${bytesToBase64Url(new Uint8Array(ciphertext))}`
}

/** Fails closed: an unsupported version, a malformed body or a wrong key all throw. */
export async function decryptDeliveryPayload(secret: string, stored: string): Promise<string> {
  const [version, ivPart, cipherPart] = stored.split('.')
  if (version !== 'v1' || ivPart === undefined || cipherPart === undefined) {
    throw new Error('Unsupported delivery payload format')
  }
  const key = await payloadKey(secret)
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBufferSource(base64UrlToBytes(ivPart)) },
    key,
    toBufferSource(base64UrlToBytes(cipherPart)),
  )
  return new TextDecoder().decode(plaintext)
}
